import { cn } from "lib/utils";
import type { ReactNode } from "react";

export type TabsItem<V extends string> = {
  value: V;
  label: ReactNode;
  /** 件数バッジ. null/undefined のとき非表示 */
  count?: number | null;
};

export type TabsProps<V extends string> = {
  value: V;
  items: ReadonlyArray<TabsItem<V>>;
  onChange: (next: V) => void;
};

/**
 * 下線型タブ. value/items/onChange の純粋表示リーフで className は受け取らない.
 * @param {TabsProps} props 値・項目配列・切替ハンドラ
 * @return {JSX.Element} タブ
 */
export const Tabs = <V extends string>({
  value,
  items,
  onChange,
}: TabsProps<V>): JSX.Element => {
  return (
    <div className="flex gap-6 border-b border-slate-200">
      {items.map(item => {
        const isActive = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            onClick={() => onChange(item.value)}
            className={cn(
              "relative px-2 py-2 text-sm font-medium transition-colors",
              isActive
                ? "text-primary"
                : "text-base-content/60 hover:text-base-content",
            )}
          >
            {item.label}
            {item.count !== null && item.count !== undefined && (
              <span
                className={cn(
                  "ml-1 text-xs",
                  isActive ? "text-primary" : "text-base-content/50",
                )}
              >
                {item.count}
              </span>
            )}
            {isActive && (
              <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary" />
            )}
          </button>
        );
      })}
    </div>
  );
};
